import { Card, Flex, SimpleTable, Typography, Tag } from "djuno-design";
import { ReactComponent as FaceSmile } from "./../assets/icons/face-smile.svg";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const TagPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Tag</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Tag component is a small label used to categorize or highlight
            an item. It supports various types, such as default, icon-based, and
            closable tags.
          </Text>

          {/* Variants and Customization */}
          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Tag component supports different variations for use cases, from
            displaying status indicators to providing interactive functionality
            with icons and close buttons. Tags can also be styled or customized
            based on the needs of the design.
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm" strong>
                Default Tags:
              </Text>
              <Text size="sm">
                The basic tag component without additional customization.
              </Text>
            </li>
            <li>
              <Text size="sm" strong>
                Icon Tags:
              </Text>
              <Text size="sm">
                Tags can include an icon placed before the text to provide more
                context or visual appeal.
              </Text>
            </li>
            <li>
              <Text size="sm" strong>
                Closable Tags:
              </Text>
              <Text size="sm">
                A tag that includes a close button to allow users to remove the
                tag when needed.
              </Text>
            </li>
            <li>
              <Text size="sm" strong>
                Color Variants:
              </Text>
              <Text size="sm">
                Tags can have various color states like processing, success,
                error, and warning to represent different statuses.
              </Text>
            </li>
            <li>
              <Text size="sm" strong>
                Borderless Tags:
              </Text>
              <Text size="sm">
                Tags can be displayed without borders for a softer look.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction={{ default: "col", lg: "row" }} className="gap-4">
            <ExpandableCard
              title="Tag"
              codeViewerProps={{
                code: TagExample1,
              }}
            >
              <Flex direction="col" className="gap-5">
                <Flex className="gap-2 w-full">
                  <Tag>Default Tag</Tag>
                  <Tag icon={<FaceSmile className="w-4 h-4" />}>Icon Tag</Tag>
                  <Tag closable>Closable Tag</Tag>
                </Flex>
                <Flex className="gap-2 w-full">
                  <Tag color="processing">processing</Tag>
                  <Tag color="success">success</Tag>
                  <Tag color="error">error</Tag>
                  <Tag color="warning">warning</Tag>
                </Flex>
                <Flex className="gap-2 w-full">
                  <Tag bordered={false}>borderless tag</Tag>
                  <Tag color="processing" bordered={false}>
                    borderless tag
                  </Tag>
                </Flex>
              </Flex>
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* color */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">color</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The color type of the tag (e.g., 'processing',
                        'success', 'error', 'warning').
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "ITag.ts#L31"}
                        size="xs"
                        code
                      >
                        PresetColorNames
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* style */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">style</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Inline styles for the tag.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        React.CSSProperties
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* icon */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">icon</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Icon to be displayed before the content.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* bordered */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">bordered</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        If false, the tag will not have a border.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">true</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* closable */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">closable</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Callback function to be called when the tag is closed.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>

                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Content to be displayed inside the tag
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>

                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* onClose */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">onClose</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Callback function to be called when the tag is closed.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`() => void`}
                      </Text>
                    </SimpleTable.TD>

                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const TagExample1 = `<Flex direction="col" className="gap-5">
    <Flex className="gap-2 w-full">
        <Tag>Default Tag</Tag>
        <Tag icon={<FaceSmile className="w-4 h-4" />}>Icon Tag</Tag>
        <Tag closable>Closable Tag</Tag>
    </Flex>
    <Flex className="gap-2 w-full">
        <Tag color="processing">processing</Tag>
        <Tag color="success">success</Tag>
        <Tag color="error">error</Tag>
        <Tag color="warning">warning</Tag>
    </Flex>
    <Flex className="gap-2 w-full">
        <Tag bordered={false}>borderless tag</Tag>
        <Tag color="processing" bordered={false}>
        borderless tag
        </Tag>
    </Flex>
</Flex>`;

export default TagPage;
